// Tracks how far the VIEWING device's clock is from the server's.
//
// The operator dashboard — and the device sub-login, which renders the very
// same sessions table — used to compute time-left by diffing a server-absolute
// `expiresAt` against the local Date.now(). That is only correct when the
// viewing device's clock is correct. It often isn't: the sub-login typically
// runs on a shop phone or tablet with no SIM and automatic date/time switched
// off, and those drift. A device running BEHIND server time reads HIGH, which
// looks exactly like "monitoring says the customer has time left but their
// portal says expired" — monitoring was the wrong one.
//
// So we note the server's own "now" on every sessions poll and shift deadlines
// by the observed skew. The local clock is then only used for the seconds
// BETWEEN two polls, where skew cancels out.
//
// This mirrors the captive portal's src/lib/serverClock.ts deliberately — the
// two countdowns must not drift apart in behaviour. Difference: the portal also
// falls back to the HTTP `Date` header, which is useless here (the dashboard
// and the API are separate origins and `Date` is not CORS-safelisted), so the
// response body's `serverNow` is the only source.

let skewMs = 0;

/** Record the server's clock from a response body carrying `serverNow`.
 *  No-op when the field is absent or unparseable — the last known skew
 *  (or 0, i.e. old behaviour) stays, so an older API can't break the timer. */
export function noteServerTime(body: unknown): void {
   const serverNow = (body as { serverNow?: string } | null | undefined)?.serverNow;
   if (!serverNow) return;
   const serverMs = new Date(serverNow).getTime();
   if (!Number.isFinite(serverMs)) return;
   skewMs = serverMs - Date.now();
}

/** Server-time deadline translated onto the local clock: safe to diff
 *  against Date.now() regardless of how wrong this device's clock is. */
export function toLocalDeadline(expiresAt: string | Date): number {
   return new Date(expiresAt).getTime() - skewMs;
}

/** Best estimate of the server's current time. */
export function estimatedServerNow(): number {
   return Date.now() + skewMs;
}

/** Observed skew in ms (server − local). Exposed for diagnostics. */
export function getClockSkewMs(): number {
   return skewMs;
}

/** Test-only reset. */
export function __resetServerClock(): void {
   skewMs = 0;
}
